/**
 * Login Page
 * Entry point for all users
 * Supports different roles and demo credentials with tenant context
 *
 * Updated to use new mock users aligned with DB schema
 */

import { useState } from 'react'
import { useNavigate, Link, useSearchParams } from 'react-router-dom'
import { AUTH_STORAGE_KEYS } from '@/types/auth.types'
import { loginDestination } from '@/security/loginDestination'
import { AlertCircle, Eye, EyeOff, LogIn, Building2, Users, User } from 'lucide-react'
import { useAuth } from '@/contexts/AuthContext'
import { mockCredentials } from '@/data/mockUsers'
import { getRoleDisplayName } from '@/types/auth.types'
import { isDomainMock } from '@/config/runtime'

// Group credentials by tenant type for display
const demoCredentialsByTenant = {
  solo: {
    label: 'Solo Firm',
    description: 'Single practitioner',
    icon: User,
    users: mockCredentials.filter(c => c.planCode === 'starter'),
  },
  medium: {
    label: 'Medium Firm',
    description: 'Up to 15 users',
    icon: Users,
    users: mockCredentials.filter(c => c.planCode === 'professional'),
  },
  large: {
    label: 'Large Firm',
    description: 'Unlimited users',
    icon: Building2,
    users: mockCredentials.filter(c => c.planCode === 'enterprise'),
  },
}

export default function LoginPage() {
  const navigate = useNavigate()
  const { login } = useAuth()
  const [searchParams] = useSearchParams()

  const [independent, setIndependent] = useState(searchParams.get('account') === 'independent')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [mfaCode, setMfaCode] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [rememberMe, setRememberMe] = useState(false)
  const [error, setError] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [selectedTenant, setSelectedTenant] = useState<'solo' | 'medium' | 'large'>('medium')

  const authenticatedDestination = () => {
    try {
      const stored = JSON.parse(
        sessionStorage.getItem(AUTH_STORAGE_KEYS.USER) ||
          localStorage.getItem(AUTH_STORAGE_KEYS.USER) ||
          '{}'
      )
      return stored.authScope === 'client_vault' ? '/client-vault' : loginDestination(stored.role)
    } catch {
      return '/dashboard'
    }
  }

  // The demo-credentials panel + "all data is mock" note only make sense when
  // auth is running on mock data; in live mode you log in with real accounts.
  const showDemoCredentials = !independent && isDomainMock('auth')

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    setIsLoading(true)

    try {
      await login(email, password, rememberMe, mfaCode.trim() || undefined, independent)
      navigate(authenticatedDestination())
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Login failed')
    } finally {
      setIsLoading(false)
    }
  }

  const handleDemoLogin = async (demoEmail: string) => {
    setEmail(demoEmail)
    setPassword('password123')
    setError('')
    setIsLoading(true)

    try {
      await login(demoEmail, 'password123', rememberMe)
      navigate(authenticatedDestination())
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Login failed')
    } finally {
      setIsLoading(false)
    }
  }

  const currentTenantData = demoCredentialsByTenant[selectedTenant]

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-600 to-primary-800 flex items-center justify-center px-4 py-8 relative overflow-hidden lg:justify-start lg:pl-32">
      {/* Background Pattern */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-primary-500 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-blob" />
        <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-primary-400 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-blob animation-delay-2000" />
      </div>

      {/* Login Card */}
      <div className="relative max-w-md w-full lg:mr-80 animate-in fade-in slide-in-from-bottom-2 duration-500">
        {/* Header */}
        <div className="text-center mb-6">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-white/10 backdrop-blur-sm shadow-lg ring-1 ring-white/20 mb-4">
            <LogIn className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-3xl font-bold tracking-tight text-white mb-2">TaxHive</h1>
          <p className="text-primary-100">Tax Practice Management Platform</p>
        </div>

        {/* Login Form Card */}
        <div className="card card-elevated rounded-2xl p-6 space-y-4">
          <div className="flex gap-2" aria-label="Sign-in account type">
            <button
              type="button"
              className="btn btn-secondary flex-1"
              aria-pressed={!independent}
              onClick={() => setIndependent(false)}
            >
              Firm account
            </button>
            <button
              type="button"
              className="btn btn-secondary flex-1"
              aria-pressed={independent}
              onClick={() => setIndependent(true)}
            >
              Client vault
            </button>
          </div>
          {!independent && (
            <Link
              to="/firm-registration"
              className="block text-sm text-primary-600 dark:text-primary-400 hover:underline"
            >
              Register a firm
            </Link>
          )}
          {independent && (
            <p className="text-sm text-text-secondary">
              Sign in to your independent client account. Your authenticator or backup code is
              required.
            </p>
          )}
          <form onSubmit={handleLogin} className="space-y-4">
            {/* Error Alert */}
            {error && (
              <div className="rounded-lg p-4 border border-red-200 bg-red-50 dark:border-red-800 dark:bg-red-900/20">
                <div className="flex items-start gap-3">
                  <AlertCircle className="w-5 h-5 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-red-700 dark:text-red-200">{error}</p>
                </div>
              </div>
            )}

            {/* Email Input */}
            <div>
              <label htmlFor="login-email" className="block text-sm font-medium text-text-primary mb-2">
                Email Address
              </label>
              <input
                id="login-email"
                type="email"
                value={email}
                onChange={e => setEmail(e.target.value)}
                placeholder="name@example.com"
                className="input input-bordered w-full"
                disabled={isLoading}
                required
              />
            </div>

            {/* Password Input */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <label htmlFor="login-password" className="block text-sm font-medium text-text-primary">Password</label>
                <Link
                  to={independent ? '/independent-account/recovery' : '/forgot-password'}
                  className="text-sm font-medium text-primary-600 hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300 hover:underline"
                >
                  Forgot password?
                </Link>
              </div>
              <div className="relative">
                <input
                  id="login-password"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  placeholder="••••••••"
                  className="input input-bordered w-full pr-10"
                  disabled={isLoading}
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-text-secondary hover:text-text-primary transition"
                  tabIndex={-1}
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            {/* Remember Me Checkbox */}
            <div>
              <label htmlFor="login-mfa" className="block text-sm font-medium text-text-primary mb-2">
                Authenticator code{' '}
                <span className="font-normal text-text-tertiary">
                  {independent ? '(required)' : '(if MFA is enabled)'}
                </span>
              </label>
              <input
                id="login-mfa"
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
                value={mfaCode}
                onChange={e => setMfaCode(e.target.value.replace(/\s/g, ''))}
                placeholder="6-digit code or backup code"
                className="input input-bordered w-full"
                disabled={isLoading}
              />
            </div>

            {/* Remember Me Checkbox */}
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="rememberMe"
                checked={rememberMe}
                onChange={e => setRememberMe(e.target.checked)}
                className="checkbox checkbox-primary checkbox-sm"
                disabled={isLoading}
              />
              <label htmlFor="rememberMe" className="text-sm text-text-secondary cursor-pointer">
                Remember me for 30 days
              </label>
            </div>

            {/* Login Button */}
            <button
              type="submit"
              disabled={isLoading || !email || !password}
              className="btn btn-primary w-full flex items-center justify-center gap-2"
            >
              {isLoading ? (
                <>
                  <span className="loading loading-spinner loading-sm" />
                  Logging in...
                </>
              ) : (
                <>
                  <LogIn className="w-4 h-4" />
                  Log In
                </>
              )}
            </button>
          </form>
          {independent && (
            <Link
              to="/independent-account/new"
              className="block text-sm text-primary-600 dark:text-primary-400 hover:underline"
            >
              Create a personal independent account
            </Link>
          )}
        </div>

        {/* Footer Info */}
        {showDemoCredentials && (
          <div className="mt-6 text-center text-sm text-primary-100">
            <p>Demo application • All data is mock</p>
          </div>
        )}
      </div>

      {/* Demo Credentials Panel - Right Bottom (mock mode only) */}
      {showDemoCredentials && (
        <div className="fixed bottom-6 right-6 w-full max-w-md px-4 sm:px-0 z-50 animate-in fade-in slide-in-from-bottom-2 duration-500">
          <div className="card card-elevated rounded-2xl glass-surface border border-surface-overlay shadow-2xl overflow-hidden">
            {/* Header */}
            <div className="p-4 border-b border-surface-overlay bg-primary-50 dark:bg-primary-900/20">
              <h3 className="text-sm font-semibold text-text-primary flex items-center gap-2">
                <span className="inline-block w-2 h-2 rounded-full bg-green-500 animate-pulse" />
                Demo Credentials
              </h3>
              <p className="text-xs text-text-tertiary mt-1">
                Select a tenant type to see available users
              </p>
            </div>

            {/* Tenant Type Tabs */}
            <div className="p-3 border-b border-surface-overlay">
              <div className="flex gap-2">
                {(Object.keys(demoCredentialsByTenant) as Array<'solo' | 'medium' | 'large'>).map(
                  key => {
                    const { label, icon: Icon } = demoCredentialsByTenant[key]
                    const isSelected = selectedTenant === key
                    return (
                      <button
                        key={key}
                        type="button"
                        onClick={() => setSelectedTenant(key)}
                        className={`flex-1 p-2 rounded-lg text-xs font-medium transition-all ${
                          isSelected
                            ? 'bg-primary-100 text-primary-700 dark:bg-primary-900/40 dark:text-primary-300'
                            : 'text-text-secondary hover:bg-surface-overlay'
                        }`}
                      >
                        <Icon className="w-4 h-4 mx-auto mb-1" />
                        {label}
                      </button>
                    )
                  }
                )}
              </div>
            </div>

            {/* User List */}
            <div className="p-4 space-y-3 max-h-80 overflow-y-auto">
              <p className="text-xs text-text-secondary">
                {currentTenantData.description} • Click to login instantly
              </p>

              <div className="space-y-2">
                {currentTenantData.users.map(cred => (
                  <button
                    key={cred.email}
                    type="button"
                    onClick={() => handleDemoLogin(cred.email)}
                    disabled={isLoading}
                    className="w-full text-left p-3 rounded-lg border border-surface-overlay hover:border-primary-400 dark:hover:border-primary-500 hover:bg-primary-50 dark:hover:bg-primary-900/20 transition-all duration-200 group"
                  >
                    <div className="flex items-center justify-between gap-3">
                      <div className="min-w-0">
                        <p className="text-sm font-semibold text-text-primary group-hover:text-primary-600 dark:group-hover:text-primary-400">
                          {cred.user.fullName}
                        </p>
                        <div className="flex items-center gap-2 mt-0.5">
                          <span className="text-xs text-text-tertiary whitespace-nowrap">
                            {getRoleDisplayName(cred.user.role)}
                          </span>
                          <span className="text-xs text-text-tertiary">•</span>
                          <span className="text-xs text-text-tertiary truncate">{cred.email}</span>
                        </div>
                      </div>
                      <LogIn className="w-4 h-4 text-text-tertiary group-hover:text-primary-600 dark:group-hover:text-primary-400 transition-colors flex-shrink-0" />
                    </div>
                  </button>
                ))}
              </div>

              {currentTenantData.users.length === 0 && (
                <p className="text-sm text-text-tertiary text-center py-4">
                  No demo users available for this tenant type
                </p>
              )}
            </div>

            {/* Footer */}
            <div className="p-3 border-t border-surface-overlay bg-surface-overlay/30">
              <p className="text-xs text-text-tertiary text-center">
                Password:{' '}
                <code className="bg-surface-overlay px-2 py-0.5 rounded-md text-primary-600 dark:text-primary-400 font-mono">
                  password123
                </code>
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
